import { useRef, useState } from "react";
import { useApp } from "../state/store";
import { useT } from "../i18n/store";
import { useFileLoader } from "../hooks/useFileLoader";
import { buildLayout, computeFitScale, contentSize } from "../domain/layout";
import { NAVBAR_HEIGHT } from "../domain/insets";
import { exportTiledPdf } from "../domain/pdfExport";
import { Icon } from "./Icon";
import { Segmented } from "./Segmented";
import { SettingsModal } from "./SettingsModal";
import type { ViewMode } from "../domain/types";

export const Navbar = () => {
  const t = useT();
  const source       = useApp((s) => s.source);
  const view         = useApp((s) => s.view);
  const setView      = useApp((s) => s.setView);
  const setBusy      = useApp((s) => s.setBusy);
  const resetViewport= useApp((s) => s.resetViewport);
  const zoomCentered = useApp((s) => s.zoomCentered);
  const zoom         = useApp((s) => s.zoom);
  const hostSize     = useApp((s) => s.hostSize);
  const paper        = useApp((s) => s.paper);
  const margins      = useApp((s) => s.margins);
  const sign         = useApp((s) => s.sign);
  const tile         = useApp((s) => s.tile);

  const zoomLabel = (() => {
    if (zoom === "fit") return t("toolbar.zoomFit.label");
    if (!source || hostSize.w === 0 || hostSize.h === 0) return "—";
    const layout = buildLayout(paper, margins, sign, tile);
    const { w, h } = contentSize(layout, view, sign);
    const visibleH = Math.max(1, hostSize.h - NAVBAR_HEIGHT);
    const fitScale = computeFitScale(hostSize.w, visibleH, w, h);
    if (fitScale <= 0) return "—";
    return `${Math.round((zoom / fitScale) * 100)}%`;
  })();

  const { handleFile, selectPdfPage } = useFileLoader();
  const fileRef = useRef<HTMLInputElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const onExport = async () => {
    const s = useApp.getState();
    if (!s.source) return;
    const layout = buildLayout(s.paper, s.margins, s.sign, s.tile);
    try {
      const { blob, filename } = await exportTiledPdf({
        state: s,
        layout,
        onProgress: (key, vars) => setBusy(t(key, vars)),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(t("alert.exportFail", { msg }));
    } finally {
      setBusy(null);
    }
  };

  const isPdfMulti = source?.type === "pdf" && source.pdfDoc.numPages > 1;

  return (
    <>
      <header className="navbar">
        <div className="navbar-bg" aria-hidden="true" />
        <div className="navbar-content">
        {/* LEFT — file info */}
        <div className="nav-left">
          {source ? (
            <div className="file-pill">
              <div className="file-name" title={source.name}>{source.name}</div>
              {isPdfMulti && (
                <select
                  className="page-select"
                  value={source.pageNum}
                  onChange={(e) => selectPdfPage(Number(e.target.value))}
                  aria-label={t("pdf.select")}
                >
                  {Array.from({ length: source.pdfDoc.numPages }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {t("pdf.pageOf", { n, total: source.pdfDoc.numPages })}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div className="file-pill empty">{t("app.noFile")}</div>
          )}
        </div>

        {/* CENTER — view + zoom */}
        <div className="nav-center">
          <Segmented<ViewMode>
            value={view}
            onChange={setView}
            options={[
              { value: "print",     label: t("toolbar.print") },
              { value: "assembled", label: t("toolbar.assembled") },
            ]}
          />
          <div className="zoomctl">
            <button
              className="icobtn"
              title={t("toolbar.zoomOut.title")}
              onClick={() => zoomCentered(1 / 1.25)}
              disabled={!source}
            >
              <Icon name="minus" size={16} />
            </button>
            <span className="zoomval mono">{zoomLabel}</span>
            <button
              className="icobtn"
              title={t("toolbar.zoomIn.title")}
              onClick={() => zoomCentered(1.25)}
              disabled={!source}
            >
              <Icon name="plus" size={16} />
            </button>
            <button
              className="icobtn"
              title={t("toolbar.zoomFit.title")}
              onClick={resetViewport}
              disabled={!source}
            >
              <Icon name="expand" size={15} />
            </button>
          </div>
        </div>

        {/* RIGHT — actions */}
        <div className="nav-right">
          <div className="cluster">
            <button className="btn" onClick={() => fileRef.current?.click()}>
              <Icon name="folder" size={16} />
              {t("app.openFile")}
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".png,image/png,.pdf,application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />

          <button className="btn primary" disabled={!source} onClick={onExport}>
            <Icon name="doc-arrow-down" size={16} strokeWidth={1.8} />
            {t("app.export")}
          </button>

          <div className="cluster">
            <button
              className="icobtn"
              onClick={() => setSettingsOpen(true)}
              title={t("app.settings")}
              aria-label={t("app.settings")}
            >
              <Icon name="settings" size={16} />
            </button>
          </div>
        </div>
        </div>
      </header>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};
